/**
 * Where a resumed application should open (SFA-8).
 *
 * ## Derived, never stored
 *
 * The obvious implementation is to write the advisor's last step into the save
 * payload and read it back. That is wrong here, and the requirement says so:
 *
 *   > resume from where the last details were provided **or where a new field
 *   > is published in the associated workflow**
 *
 * A stored pointer only satisfies the first clause. An application saved on
 * step 4 whose originator then publishes a new required field on step 2 would
 * reopen on step 4 — walking the advisor straight past the field they now have
 * to answer. Deriving the position from the values instead handles both clauses
 * with one rule, because a newly published required field simply makes an
 * earlier step incomplete again.
 *
 * It also keeps spec §14 intact: nothing about an in-flight application is
 * persisted on our side, and BMO's save contract needs no new property.
 *
 * ## The rule
 *
 * The first visible step that is not complete. "Complete" is the caller's
 * definition — `FormShell.isStepComplete`, the same predicate that paints the
 * rail checks and gates Next — so the resume point can never disagree with what
 * the rail shows. Steps whose runtime completion semantics don't exist yet
 * (ApplicationReview, PdfGeneration, OneSpanESign) report incomplete, which is
 * what stops a fully-answered application at its review step instead of
 * dropping the advisor on ThankYou.
 *
 * ## Why the steps behind it come back too
 *
 * FormShell locks any step the advisor has not advanced through. Opening a
 * resumed application on step 4 with an empty advanced-set would leave steps
 * 1-3 unreachable — the advisor could see their answers in the rail and not
 * open them. SFA-8 requires the opposite ("modify ALL the details provided"),
 * so everything before the resume point is marked advanced.
 */

/** A step as far as this module cares — id plus whatever the predicates need. */
export type ResumeStep = { id: string };

export type ResumePosition = {
  /** Step to open on. Null only when the workflow has no visible steps. */
  stepId: string | null;
  /** Steps before `stepId`, which must be unlocked so the advisor can go back. */
  priorStepIds: string[];
};

export function deriveResumePosition<T extends ResumeStep>(
  steps: readonly T[],
  isVisible: (step: T) => boolean,
  isComplete: (step: T) => boolean,
): ResumePosition {
  const visible = steps.filter(isVisible);
  if (visible.length === 0) return { stepId: null, priorStepIds: [] };

  const target = visible.findIndex((step) => !isComplete(step));

  // Every visible step complete: the advisor answered everything and stopped
  // without submitting. Open the last step rather than the first — resuming an
  // application at its beginning when nothing is outstanding would make the
  // advisor walk the whole form again to reach the action they left off at.
  const index = target === -1 ? visible.length - 1 : target;

  return {
    stepId: visible[index]!.id,
    priorStepIds: visible.slice(0, index).map((step) => step.id),
  };
}
